<template>
  <v-form ref="report-form" v-model="formValid">
    <div class="mb-3">
      <div class="report-header-info-board">
        <v-chip v-if="internalReport.validated" color="success" text-color="white" small>Validated</v-chip>
        <v-chip v-if="internalReport.sentAt" class="ml-2" label color="primary" outlined small>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                          <span v-on="on">
                            <i class="fas fa-envelope-square"></i>&nbsp;{{ internalReport.sentAt | DateWithMinutes }}
                          </span>
            </template>
            Sent to client at {{ internalReport.sentAt | DateWithMinutes }}
          </v-tooltip>
        </v-chip>
      </div>
      <div class="report-header-buttons">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn small text icon @click="handleDeleteReport"
                   :disabled="!canDeleteReport"
                   :loading="progress('deleteReport')"
                   v-on="on">
              <v-icon small>fas fa-trash</v-icon>
            </v-btn>
          </template>
          Delete report
        </v-tooltip>
      </div>
    </div>
    <div>
      <b>Files</b>
      <file-list :items="reportFiles" @delete="handleDeleteFile" @fileClick="fileViewerOpen($event)">
        <template v-slot:no-items>
          <div class="text-center">No files</div>
        </template>
      </file-list>
      <div style="clear: both;"></div>

      <div class="report-file-upload text-right">
        <file-upload
            ref="fileUploader"
            :input-id="reportFileUploadId"
            v-model="reportFiles"
            :post-action="uploadUrl"
            multiple
            :customAction="onReportFileUpload"
            @input-file="handleReportFileUploaderInputFile">
          <v-btn small color="primary">Add file</v-btn>
        </file-upload>
      </div>
    </div>
    <div class="mt-3">
      <b>Samples</b>
      <samples-list ref="samples-list"
                    :samples="internalReport.samples"
                    :readonly="readonly"
                    @validation-changed="handleSampleListValidationChanged"
                    @delete="handleDeleteSample"
                    @move-down="handleMoveDown"
                    @move-up="handleMoveUp"
      ></samples-list>
      <div>
        <v-menu v-model="samplesMenuVisible"
                absolute
                offset-y
                min-width="400"
                :close-on-content-click="false">
          <template v-slot:activator="{ on }">
            <v-btn color="primary"
                   small
                   :disabled="readonly"
                   v-on="on">
              Add sample
            </v-btn>
          </template>
          <v-card>
            <v-card-text class="text--primary">
              <v-autocomplete
                  :items="availableSamples"
                  :disabled="readonly"
                  @input="handleAddSample"
                  item-text="title"
                  label="Please select sample"
                  item-value="id"
                  :menu-props="{'maxWidth':'400', 'minWidth': 400, 'nudge-left': 16}">
              </v-autocomplete>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </div>
    <div class="mt-3">
      <v-textarea
          class=""
          label="Comment"
          :rules="commentRules"
          :disabled="readonly"
          auto-grow
          v-model="comment"
          :append-icon="commentOptionsVisible ? 'fas fa-caret-up' : 'fas fa-caret-down'"
          @click:append="handleCommentSelectorClick">
      </v-textarea>
      <v-menu v-model="commentOptionsVisible"
              max-height="500"
              :position-x="commentMenuX"
              :position-y="commentMenuY">
        <v-list>
          <v-list-item v-for="commentOption in commentOptions"
                       :key="commentOption"
                       @click="handleAppendComment(commentOption)">
            <v-list-item-content>
              <v-list-item-title v-text="commentOption"></v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <div class="mt-3">
      <b>Specialists</b>
      <div>
        <specialist-list :items="internalReport.specialists"
                         :can-sign="canSignReport"
                         :readonly="readonly"
                         ref="specialist-list"
                         @delete="handleDeleteSpecialist"
                         @onSign="handleOnSign"
                         @onDeleteSign="handleOnDeleteSign"
                         @onBeforeSign="handleOnBeforeSign"></specialist-list>
      </div>
      <v-menu v-model="specialistsMenuVisible"
              absolute
              offset-y
              min-width="400"
              hide-selected
              :close-on-content-click="false">
        <template v-slot:activator="{ on: menu }">
          <v-tooltip bottom :disabled="canAddSpecialists">
            <template v-slot:activator="{ on: tooltip }">
                            <span v-on="{ ...tooltip }">
                            <v-btn color="primary"
                                   :disabled="!canAddSpecialists"
                                   :loading="progress('addSpecialistToReport')"
                                   small
                                   v-on="{ ...menu }">
                                Add specialist
                            </v-btn>
                            </span>
            </template>
            <span>For add specialists you need to create report first</span>
          </v-tooltip>
        </template>
        <v-card>
          <v-card-text class="text--primary">
            <v-autocomplete
                :disabled="progress('addSpecialistToReport')"
                :loading="progress('addSpecialistToReport')"
                :items="availableSpecialists"
                @input="handleAddSpecialist"
                item-text="name"
                label="Please select specialist"
                item-value="id"
                :menu-props="{'maxWidth':'400', 'minWidth': 400, 'nudge-left': 16}">
            </v-autocomplete>
          </v-card-text>
        </v-card>
      </v-menu>
    </div>
    <v-divider class="mt-3 mb-3"></v-divider>
    <div class="mt-3">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <b v-on="on">Override report</b>
        </template>
        <span>Docx or pdf only!</span>
      </v-tooltip>
      <div>
        <FileItem v-if="internalReport.overrideReport" :file="internalReport.overrideReport"
                  :readonly="readonly" :direct-download="true"
                  @delete="internalReport.overrideReport = null"/>
      </div>
      <div style="clear: both;"></div>
      <div class="report-file-upload">
        <file-upload
            ref="overrideReportUploader"
            :input-id="overrideReportUploadId"
            v-model="overrideReport"
            :post-action="uploadUrl"
            :disabled="readonly"
            :customAction="onFileUpload"
            @input-file="handleOverrideReportUploaderInputFile">
          <v-btn small color="primary" :disabled="readonly">Upload report</v-btn>
        </file-upload>
      </div>
    </div>
    <v-divider class="mt-3 mb-3"></v-divider>
    <v-alert
        :value="!!report.overrideReport"
        type="info"
    >
      Pay attention, the report has been overridden manually!
    </v-alert>
    <v-alert
        :value="report.snapshotDiff !== null"
        :icon="false"
        type="warning"
    >
      <v-row>
        <v-col cols="8" class="mt-2">
          Data in case and report are different. To actualize data in report, press button
        </v-col>
        <v-col cols="4" class="text-right">
          <v-btn color="primary"
                 v-if="report.snapshotDiff !== null"
                 @click="handleActualizeReportButtonClick">
            <span>Actualize data</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>
    <div class="text-right">
      <v-btn color="primary"
             class="ml-3"
             v-if="internalReport.validated"
             :disabled="!canSendReport"
             :loading="progress('sendReport')"
             @click="handleSendReportButtonClick">
        <v-icon left small>fas fa-paper-plane</v-icon>
        <span v-if="internalReport.sentAt">Resend</span><span
          v-else>Send</span>
      </v-btn>
      <v-btn v-if="canShowReport"
             class="ml-3"
             color="primary"
             @click="handleShowReport">
        <v-icon left small>fas fa-eye</v-icon>
        Show
      </v-btn>
      <v-btn v-if="canDownload"
             class="ml-3"
             :loading="progress('downloadReport')"
             :disabled="progress('downloadReport')"
             color="primary"
             @click="handleDownloadPdfButtonClick">
        <v-icon left small>fas fa-download</v-icon>
        Pdf
      </v-btn>
      <v-menu
          top
          :close-on-click="true"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
              class="ml-3"
              :loading="progress('downloadReport')"
              :disabled="docxButtonDisabled"
              color="primary"
              v-bind="attrs"
              v-on="on"
          >
            <v-icon left small>fas fa-download</v-icon>
            Docx
          </v-btn>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title @click="handleDownloadDocxButtonClick(false)">With signatures</v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title @click="handleDownloadDocxButtonClick(true)">Without signatures</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn color="success" v-if="internalReport.id"
             class="ml-3"
             :loading="progress('updateReport')"
             :disabled="updateButtonDisabled"
             @click="handleUpdateReportButtonClick">Update
      </v-btn>
      <v-btn v-else
             class="ml-3"
             color="success"
             :loading="progress('createReport')"
             :disabled="!isValid() || progress('createReport')"
             @click="handleCreateReportButtonClick">Create
      </v-btn>
      <confirm ref="confirm"></confirm>
      <v-dialog v-if="snapshotDiffManagerVisible"
                v-model="snapshotDiffManagerVisible"
                @keydown.esc="handleCloseSnapshotDiffManager"
                max-width="800">
        <snapshot-diff-manager :diff="report.snapshotDiff"
                               @confirm="handleConfirmSnapshotDiff"
                               :confirm-in-progress="commonProgress('pgtReports.actualizeReportData')"
                               @cancel="handleCloseSnapshotDiffManager"></snapshot-diff-manager>
      </v-dialog>
    </div>
  </v-form>

</template>

<script>
import _ from 'lodash'
import FileList from '../Files/List'
import FileItem from '../Files/Item'
import SpecialistList from './SpecialistList'
import SamplesList from './SamplesList'
import * as AvailableSampleTargets from '../../dictionaries/sample-targets'
import FileSaver from 'file-saver'
import {getBaseApiURL} from '@/helpers/baseurl'
import DateWithMinutes from '../Filters/DateWithMinutes'
import Confirm from '../Common/Confirm'
import {mapActions, mapGetters} from 'vuex'
import SnapshotDiffManager from '@/components/Reports/SnapshotDiffManager'
import moment from 'moment'
import {actualizeReportData} from "@/store/modules/pgtcases/actions/common";

export default {
  name: 'Item',
  props: {
    report: Object,
    caseData: Object,
    caseUnsavedChangesChecker: Function
  },
  components: {
    FileList,
    FileItem,
    SpecialistList,
    SamplesList,
    Confirm,
    SnapshotDiffManager,
  },
  filters: {
    DateWithMinutes
  },
  watch: {
    report(newReport) {
      // Внимание! Хитрая логика! Сравниваем обновленное заключение с текущим без учета файлов
      // для того, чтобы понять надо обновлять только файлы в reportFiles или все заключение целиком
      let newReportNormalized = this.initInternalReport(newReport)
      const newReportWithoutFiles = _.omit(newReportNormalized, ['files'])
      const originalReportWithoutFiles = _.omit(this.originalReportData, 'files')

      // Если в заключении изменились только файлы, заменяем их во всех внутренних структурах
      if (_.isEqual(newReportWithoutFiles, originalReportWithoutFiles)) {
        let canUpdateReportFiles = true
        // если хотя бы 1 файл еще загружается, то обновление текущих reportFiles не делаем, чтобы не удалить
        // рабочие объекты загрузчика
        for (let reportFile of this.reportFiles) {
          if (reportFile.success === false) {
            canUpdateReportFiles = false
          }
        }

        if (canUpdateReportFiles) {
          this.reportFiles = _.cloneDeep(newReportNormalized.files)
        }
        this.originalReportData.files = _.map(newReportNormalized.files, 'fileId')
        this.internalReport.files = _.map(newReportNormalized.files, 'fileId')
        return
      }

      this.internalReport = this.initInternalReport(newReport)
      this.originalReportData = this.getReportData()
    },
    'internalReport.overrideReport': _.debounce(function (overrideReport) {
      // Если произошло удаление перегруженного заключения - обновим заключение
      if (overrideReport === null) {
        this.validate()
        if (!this.isSubFormsValid()) {
          return
        }
        this.updateReport()
        return
      }

      const success = _.get(overrideReport, 'success', false)

      if (!success) {
        return
      }

      // Если произошла успешная загрузка заключения - также обновим заключение
      this.validate()
      if (!this.isSubFormsValid()) {
        return
      }
      this.updateReport()
    }, 200)
  },
  data() {
    return {
      formValid: false,
      subFormRefs: ['samples-list'],
      reportFileUploadId: _.uniqueId('file-upload-'),
      overrideReportUploadId: _.uniqueId('override-report-upload-'),
      internalReport: this.initInternalReport(this.report),
      originalReportData: null,
      specialistsMenuVisible: false,
      samplesMenuVisible: false,
      reportFiles: this.report.files || [],
      commentOptionsVisible: false,
      commentMenuX: null,
      commentMenuY: null,
      snapshotDiffManagerVisible: false,
    }
  },
  computed: {
    ...mapGetters({
      commonProgress: 'progress'
    }),
    ...mapGetters('pgtCases', {
      progress: 'progress',
      reportSampleTemplatesById: 'reportSampleTemplatesById'
    }),
    ...mapGetters({
      availableSpecialistsByIds: 'availableSpecialistsByIds',
      canSignReportsSpecialists: 'canSignReportsSpecialists'
    }),
    readonly() {
      return !!this.internalReport.sentAt
    },
    updateButtonDisabled() {
      return !this.isValid() || this.progress('updateReport') || this.readonly
    },
    caseSamplesByIds() {
      const caseSamples = _.get(this.caseData, 'samples', [])
      return _.keyBy(caseSamples, 'id')
    },
    availableSpecialists() {
      return _.filter(this.canSignReportsSpecialists, (s) => {
        return _.findIndex(this.internalReport.specialists, ['id', s.id]) < 0
      })
    },
    availableSamples() {
      const samples = _.get(this.caseData, 'samples', [])
      return _.filter(samples, (s) => {
        if (s.target !== AvailableSampleTargets.Analysis) {
          return false
        }

        return _.findIndex(this.internalReport.samples, ['id', s.id]) < 0
      })
    },
    uploadUrl() {
      return `${getBaseApiURL()}files/upload`
    },
    canWriteReport() {
      return !!this.caseData
    },
    canDownload() {
      return !!this.internalReport.id
    },
    canShowReport() {
      return !!this.internalReport.id
    },
    canSignReport() {
      return !!this.internalReport.id
    },
    canSendReport() {
      return !this.progress('sendReport') && this.caseData.allDocumentsReceived
    },
    canAddSpecialists() {
      return !!this.internalReport.id && !this.progress('addSpecialistToReport') && !this.readonly
    },
    canDeleteReport() {
      return !this.internalReport.id || this.internalReport.status === 'created'
    },
    commentRules() {
      let commentRequired = false
      for (let sample of this.internalReport.samples) {
        let template = _.get(this.reportSampleTemplatesById, _.get(sample, 'templateId', null), null)

        if (template === null) {
          continue
        }

        if (template.needComment) {
          commentRequired = true
          break
        }
      }

      if (commentRequired) {
        return [
          v => !!v || 'Please, enter comment'
        ]
      }

      return []
    },
    comment: {
      get() {
        return this.internalReport.comment
      },
      set(value) {
        this.internalReport.comment = value.replace(/\r?\n/g, "\r\n")
      }
    },
    overrideReport: {
      get() {
        if (this.internalReport.overrideReport === null) {
          return []
        }

        return [this.internalReport.overrideReport]
      },
      set(value) {
        this.internalReport.overrideReport = value[0]
      }
    },
    docxButtonDisabled() {
      const mimeType = _.get(this.report, ['overrideReport', 'mimeType'], null)
      return this.progress('downloadReport') || mimeType === 'application/pdf'
    },
    reportViewerTitle() {
      const systemId = _.get(this.caseData, 'systemId', '#')
      const reportOrder = _.get(this.internalReport, 'order', '')

      return `${systemId} #${reportOrder}`
    },
    commentOptions() {
      return [
        '* Отсутствие амплификации.',
        '* Деградация биоматериала.',
        '* Достоверность данных может быть снижена в связи с контаминацией контроля к образцу.',
        '* После очной консультации врача-генетика.',
        '* С точки зрения качества полученных данных эмбрион, соответствующий образцу T, рекомендован к переносу в первую очередь, а эмбрион, соответствующий образцу T, – во вторую очередь.',
        '* С точки зрения качества полученных данных эмбрион, соответствующий образцу T, рекомендован к переносу в последнюю очередь.',
        '* С точки зрения качества полученных данных эмбрионы, соответствующие образцам T, T, рекомендованы к переносу в первую очередь, а эмбрионы, соответствующие образцам T, T, – во вторую очередь.',
        'По результатам ПГТ-А можно предположить, что один из партнеров является носителем сбалансированной хромосомной перестройки. Рекомендовано кариотипирование обоих партнеров.',
        '* Кариотип 47,XXY соответствует синдрому Кляйнфельтера. Решение пациента о переносе данного эмбриона может быть принято только после очной консультации врача-генетика.',
        '* Кариотип 47,XXY/XY соответствует мозаичной форме синдрома Кляйнфельтера. Решение пациента о переносе данного эмбриона может быть принято только после очной консультации врача-генетика.',
        'Сегментарные дупликации хромосомы Y c высокой долей вероятности являются унаследованными.'
      ]
    }
  },
  mounted() {
    this.originalReportData = this.getReportData()
  },
  methods: {
    ...mapActions({
      fileViewerOpen: 'fileViewerOpen'
    }),
    ...mapActions('pgtCases', {
      deleteReport: 'deleteReport',
      downloadReport: 'downloadReport',
      sendReport: 'sendReport',
      signReport: 'signReport',
      deleteSignFromReport: 'deleteSignFromReport',
      addSpecialistToReport: 'addSpecialistToReport',
      deleteSpecialistFromReport: 'deleteSpecialistFromReport',
      addFileToReport: 'addFileToReport',
      deleteFileFromReport: 'deleteFileFromReport',
      actualizeReportData: 'actualizeReportData'
    }),
    handleDeleteFile(fileId) {
      // Если заключение уже создано, то удаляем файлы напрямую, через метод API
      if (this.report.id) {
        this.deleteFileFromReport({reportId: this.report.id, fileId: fileId})
        return
      }

      // Если заключение не создано, тогда удаляем файлы из внутреннего массива

      let fileIndex = _.findIndex(this.reportFiles, function (file) {
        return file.fileId === fileId
      })

      if (fileIndex < 0) {
        return
      }

      this.reportFiles.splice(fileIndex, 1)
    },
    handleReportFileUploaderInputFile(newFile, oldFile) {
      // Automatic upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.fileUploader.active) {
          this.$refs.fileUploader.active = true
        }
      }
    },
    handleOverrideReportUploaderInputFile(newFile, oldFile) {
      // Automatic upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.overrideReportUploader.active) {
          this.$refs.overrideReportUploader.active = true
        }
      }
    },
    async onFileUpload(file, component) {
      let result = await component.uploadHtml5(file)
      const response = result.response

      if (response.error !== null) {
        let resultErrors = []

        if (_.isString(response.error)) {
          resultErrors.push(response.error)
        }

        if (_.isObject(response.error)) {
          for (let key in response.error) {
            if (!response.error.hasOwnProperty(key)) {
              continue
            }

            resultErrors.push(response.error[key])
          }
        }
        result.error = _.join(resultErrors, '; ')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)
        return result
      }

      let fileId = _.get(response.data, 'fileId', null)

      if (fileId === null) {
        result.error = this.$t('files.errors.unableToUploadFile')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)

        return result
      }

      result.fileId = fileId
      result.mimeType = _.get(response.data, 'mimeType', null)

      return result
    },
    async onReportFileUpload(file, component) {
      const result = await this.onFileUpload(file, component)

      if (this.report.id) {
        this.addFileToReport({reportId: this.report.id, fileId: result.fileId})
      }
      return result
    },
    async handleAddSpecialist(specialistId) {
      const isAdded = await this.addSpecialistToReport({
        reportId: this.internalReport.id,
        specialistId: specialistId
      })

      if (isAdded) {
        this.specialistsMenuVisible = false
      }


      this.internalReport.specialists = _.orderBy(this.internalReport.specialists, ['signOrder'], ['asc'])
    },
    async handleDeleteSpecialist(specialistId) {
      const isDeleted = await this.deleteSpecialistFromReport({
        reportId: this.internalReport.id,
        specialistId: specialistId
      })

      this.internalReport.specialists = _.orderBy(this.internalReport.specialists, ['signOrder'], ['asc'])
    },
    handleAddSample(sampleId) {
      const sample = _.get(this.caseSamplesByIds, sampleId, null)

      if (!sample) {
        return
      }

      const newSample = {
        id: sampleId,
        caseId: this.caseData.id,
        title: sample.title,
        orderNumber: sample.orderNumber,
        karyotype: null,
        templateId: null,
        interpretation: null,
        recommendations: null,
      }

      let position = this.calculateSamplePosition(newSample)

      let newSamples = _.slice(this.internalReport.samples, 0, position)

      newSamples.push(newSample)

      this.internalReport.samples = _.concat(newSamples, _.slice(this.internalReport.samples, position))

      this.samplesMenuVisible = false
    },
    calculateSamplePosition(newSample) {
      if (this.internalReport.samples.length === 0) {
        return 0
      }

      const samples = _.cloneDeep(this.internalReport.samples)
      samples.push(newSample)

      let sortedSamples = _.orderBy(samples, ['orderNumber', (s) => {
        return s.title.length
      }], ['asc', 'asc'])

      const index = _.findIndex(sortedSamples, function (s) {
        return s.id === newSample.id
      })

      let resultPosition = null

      if (index > 0) {
        let leftNeighbourSorted = sortedSamples[index - 1]

        let leftNeighbourOriginalIndex = _.findIndex(samples, function (s) {
          return s.id === leftNeighbourSorted.id
        })

        resultPosition = leftNeighbourOriginalIndex + 1
      }

      if (index === 0) {
        let rightNeighbourSorted = sortedSamples[index + 1]

        resultPosition = _.findIndex(samples, function (s) {
          return s.id === rightNeighbourSorted.id
        })
      }

      if (resultPosition < 0) {
        resultPosition = 0
      }

      return resultPosition
    },
    handleDeleteSample(sampleId) {
      const index = _.findIndex(this.internalReport.samples, (s) => {
        return s.id === sampleId
      })

      if (index < 0) {
        return
      }

      this.internalReport.samples.splice(index, 1)
    },
    isSubFormsValid() {
      let validationResult = true

      for (let subFormRef of this.subFormRefs) {
        const subForm = _.get(this.$refs, subFormRef)

        if (!subForm) {
          continue
        }

        if (!subForm.isValid()) {
          validationResult = false
          break
        }
      }

      return validationResult
    },
    validateSubForms() {
      let validationResult = true

      for (let subFormRef of this.subFormRefs) {
        const subForm = _.get(this.$refs, subFormRef)

        if (!subForm) {
          continue
        }

        if (!subForm.validate()) {
          validationResult = false
        }
      }

      return validationResult
    },
    isValid() {
      return this.formValid && this.isSubFormsValid()
    },
    validate() {
      let reportFormValidateResult = true

      if (this.$refs['report-form']) {
        reportFormValidateResult = this.$refs['report-form'].validate()
      }
      return reportFormValidateResult && this.validateSubForms()
    },
    handleSampleListValidationChanged(value) {
      this.validate()
    },
    handleCreateReportButtonClick() {
      const reportData = this.getReportData()
      this.$emit('create-report', reportData)
    },
    handleUpdateReportButtonClick() {
      this.processActionAfterUnsavedChangesCheck(() => {
        if (this.internalReport.validated) {
          this.$refs.confirm.open('Update report after validation', `You are trying to update a report after it has been validated. Signatures of specialists will be deleted. Do you really want to continue?`, {width: 600}).then(async (confirm) => {
            if (!confirm) {
              return
            }
            this.updateReport()
          })
          return
        }
        this.updateReport()
      }, false, true)
    },
    updateReport() {
      const reportData = this.getReportData()
      this.$emit('update-report', reportData)
    },
    getReportData() {
      return {
        ..._.cloneDeep(this.internalReport),
        files: _.map(this.reportFiles, 'fileId'),
        overrideReport: _.get(this.internalReport.overrideReport, 'fileId', null)
      }
    },
    initInternalReport(originalReport) {
      let internalReportSpecialists = []
      for (let specialist of originalReport.specialists) {

        let availableSpecialist = _.get(this.$store.getters.availableSpecialistsByIds, specialist.id)

        internalReportSpecialists.push({
          id: availableSpecialist.id,
          signed: specialist.signed,
          name: availableSpecialist.name,
          pinCode: availableSpecialist.pinCode,
          signOrder: availableSpecialist.signOrder,
        })
      }

      internalReportSpecialists = _.orderBy(internalReportSpecialists, ['signOrder'], ['asc'])

      let preparedSamples = []
      const samples = _.get(originalReport, 'samples', [])

      const caseSamples = _.get(this.caseData, 'samples', [])
      const caseSamplesByIds = _.keyBy(caseSamples, 'id')

      for (let sample of samples) {
        const caseSample = _.get(caseSamplesByIds, sample.id, null)

        if (!caseSample) {
          continue
        }

        preparedSamples.push({
          ...sample,
          title: caseSample.title,
          orderNumber: caseSample.orderNumber,
          caseId: caseSample.caseId
        })
      }

      return {
        id: _.cloneDeep(_.get(originalReport, 'id', null)),
        caseId: _.cloneDeep(_.get(this.caseData, 'id')),
        files: _.cloneDeep(_.get(originalReport, 'files', [])),
        overrideReport: _.cloneDeep(_.get(originalReport, 'overrideReport', null)),
        specialists: internalReportSpecialists,
        samples: _.cloneDeep(preparedSamples),
        order: _.cloneDeep(_.get(originalReport, 'order', null)),
        validated: _.cloneDeep(_.get(originalReport, 'validated', false)),
        sentAt: _.cloneDeep(_.get(originalReport, 'sentAt', null)),
        comment: _.cloneDeep(_.get(originalReport, 'comment', null)),
        status: _.cloneDeep(_.get(originalReport, 'status', null))
      }
    },
    async handleDownloadPdfButtonClick() {
      this.processActionAfterUnsavedChangesCheck(this.downloadPdfReport)
    },
    async processActionAfterUnsavedChangesCheck(action, ignoreCase, ignoreReport) {
      const caseChanged = this.caseUnsavedChangesChecker()

      let caseNext = true
      let reportNext = true

      if (caseChanged && !ignoreCase) {
        await this.$refs.confirm.open('Unsaved case changes', `You have unsaved case changes. These changes will not be displayed in the report. Do you really want to continue?`, {width: 600}).then(async (confirm) => {
          if (!confirm) {
            caseNext = false
          }
        })
      }

      const reportChanged = this.checkReportUnsavedChanges()

      if (reportChanged && !ignoreReport) {
        await this.$refs.confirm.open('Unsaved report changes', `You have unsaved report changes. Do you really want to continue?`, {width: 600}).then(async (confirm) => {
          if (!confirm) {
            reportNext = false
          }
        })
      }

      if (caseNext && reportNext) {
        await action()
      }
    },
    async downloadPdfReport() {
      try {
        let reportData = await this.downloadReport({reportId: this.report.id, format: 'pdf'})

        if (!reportData) {
          this.$snotify.error(`Unable to get report. Please try again later`)
          return
        }

        let blob = new Blob([reportData], {type: 'application/pdf'})

        FileSaver.saveAs(blob, this.buildReportFileName('pdf'))
      } catch (e) {
        console.log(`Unable to download report, details: ${e.message}`)
      }
    },
    async handleDownloadDocxButtonClick(withoutSignatures = false) {
      this.processActionAfterUnsavedChangesCheck(_.partial(this.downloadDocxReport, withoutSignatures, withoutSignatures))
    },
    async downloadDocxReport(withoutSignatures = false, withoutStamp = false) {
      try {
        let reportData = await this.downloadReport({
          reportId: this.report.id,
          format: 'docx',
          withoutSignatures: withoutSignatures,
          withoutStamp: withoutStamp
        })

        if (!reportData) {
          this.$snotify.error(`Unable to get report. Please try again later`)
          return
        }

        let blob = new Blob([reportData], {type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'})

        FileSaver.saveAs(blob, this.buildReportFileName('docx'))
      } catch (e) {
        console.log(`Unable to download report, details: ${e.message}`)
      }
    },
    buildReportFileName(reportType) {
      let extension = ''
      switch (reportType) {
        case 'pdf':
          extension = '.pdf'
          break
        case 'docx':
          extension = '.docx'
          break
      }
      const patientName = _.get(this.caseData, 'patient.name', 'patient_name_unknown')
      const formattedPatientName = _.replace(patientName, new RegExp(" ", "g"), "_")
      const caseSystemId = _.get(this.caseData, 'systemId', '')
      const reportDate = moment().format("YYYY-MM-DD-HH-mm-ss")

      return `${formattedPatientName}-PGT${caseSystemId}-${reportDate}${extension}`
    },
    handleSendReportButtonClick() {
      this.processActionAfterUnsavedChangesCheck(() => {
        this.sendReport(this.report.id)
      })
    },
    async handleOnSign(specialistId) {
      this.processActionAfterUnsavedChangesCheck(async () => {
        await this.signReport({reportId: this.internalReport.id, specialistId: specialistId})
      })
    },
    async handleOnDeleteSign(specialistId) {
      await this.deleteSignFromReport({reportId: this.internalReport.id, specialistId: specialistId})
    },
    checkReportUnsavedChanges() {
      const reportData = this.getReportData()
      return !_.isEqual(this.originalReportData, reportData)
    },
    async handleOnBeforeSign(next) {
      this.processActionAfterUnsavedChangesCheck(next)
    },
    async doActionAfterReportUnsavedChangesCheck(action) {
      await this.processActionAfterUnsavedChangesCheck(action, true, false)
    },
    handleMoveDown(sampleId) {
      const sampleIndex = _.findIndex(this.internalReport.samples, (s) => {
        return s.id === sampleId
      })

      let samples = _.cloneDeep(this.internalReport.samples)

      let tmp = samples[sampleIndex - 1]
      samples[sampleIndex - 1] = samples[sampleIndex]
      samples[sampleIndex] = tmp

      this.internalReport.samples = samples
    },
    handleMoveUp(sampleId) {
      const sampleIndex = _.findIndex(this.internalReport.samples, (s) => {
        return s.id === sampleId
      })

      let samples = _.cloneDeep(this.internalReport.samples)

      let tmp = samples[sampleIndex + 1]
      samples[sampleIndex + 1] = samples[sampleIndex]
      samples[sampleIndex] = tmp

      this.internalReport.samples = samples
    },
    handleShowReport() {
      this.fileViewerOpen({
        source: `${getBaseApiURL('v1')}/reports/download?id=${this.internalReport.id}&format=pdf`,
        title: this.reportViewerTitle,
        downloadFileName: this.buildReportFileName('pdf'),
        mimeType: 'application/pdf'
      })
    },
    handleDeleteReport() {
      this.$refs.confirm.open('Report deletion', `This report will be deleted. Do you really want to continue?`, {width: 600}).then(async (confirm) => {
        if (!confirm) {
          return
        }

        this.$emit('delete-report', this.internalReport)
      })
    },
    handleCommentSelectorClick(e) {
      this.commentMenuX = e.clientX
      this.commentMenuY = e.clientY
      this.$nextTick(() => {
        this.commentOptionsVisible = true
      })
    },
    handleAppendComment(comment) {
      if (this.internalReport.comment === null || _.trim(this.internalReport.comment).length === 0) {
        this.internalReport.comment = comment

        return
      }

      this.internalReport.comment += `\r\n${comment}`
    },
    handleActualizeReportButtonClick() {
      this.snapshotDiffManagerVisible = true
    },
    handleCloseSnapshotDiffManager() {
      this.snapshotDiffManagerVisible = false
    },
    async handleConfirmSnapshotDiff(params) {
      const actualized = await this.actualizeReportData({
        reportId: this.report.id,
        reason: params.reason
      })

      if (actualized) {
        this.snapshotDiffManagerVisible = false
      }
    }
  }
}
</script>

<style scoped>
.report-file-upload i {
  font-size: 44px;
  color: #1976d2
}

.report-file-upload > div {
  margin-top: 50px;
  cursor: pointer;
}

.report-header-info-board {
  display: inline-block;
}

.report-header-buttons {
  display: inline-block;
  float: right;
}
</style>